/**
 * handlers.ts — the offline-queue registration FACADE.
 *
 * Charter: ONE thing — call `registerOfflineHandlers()` once at app init (after
 * startOfflineQueue) and every domain's replay handlers are wired. This module
 * holds NO handler of its own; each queue type is owned by the sibling module for
 * its bounded context:
 *
 *   ./handlers.appointments  — Termine: create / edit / cancel
 *   ./handlers.visitReports  — what a rep submits FROM a visit: the Besuchsbericht
 *                              (legacy / v2 / v8) + the Bestellschein confirm.
 *                              Owns DELETE-ON-VERIFIED-ACK.
 *   ./handlers.missionDay    — the running mission day: lifecycle, joker stop,
 *                              lead status
 *   ./handlers.planning      — planning a day or a week: stop reorder, mission
 *                              date, rep mission-wishes
 *   ./handlers.callResult    — the prequal/calling call-result INSERT
 *
 * Dependency direction is one-way: facade → domain module → `./queueTypes`
 * (the shared key contract). This file is the ONLY import surface outsiders use —
 * `QUEUE_TYPES` and every payload type are re-exported below, so all existing
 * `@appic/lib/offline/handlers` import sites keep working unchanged.
 */

import { registerAppointmentHandlers } from './handlers.appointments'
import { registerVisitReportHandlers } from './handlers.visitReports'
import { registerMissionDayHandlers } from './handlers.missionDay'
import { registerPlanningHandlers } from './handlers.planning'
import { registerCallResultHandlers } from './handlers.callResult'

// ── The public surface (re-exported; owned by the modules above) ────────

export { QUEUE_TYPES } from './queueTypes'

export type {
  AppointmentCreatePayload,
  AppointmentUpdatePayload,
  AppointmentCancelPayload,
} from './handlers.appointments'

export type {
  VisitReportQueuePayload,
  VisitReportV2QueuePayload,
  VisitReportV8QueuePayload,
  ScanOrderConfirmQueuePayload,
} from './handlers.visitReports'

export type {
  LeadStatusUpdatePayload,
  JokerStopCreatePayload,
  MissionDayLifecyclePayload,
} from './handlers.missionDay'

export type {
  MissionLeadReorderPayload,
  MissionDateUpdatePayload,
  RepPreferencesSavePayload,
} from './handlers.planning'

export type { CallResultSubmitQueuePayload } from './handlers.callResult'

// ── Registration ───────────────────────────────────────────────────────

/**
 * Wire every domain's queue handlers. Idempotent in the same sense the
 * individual registrations are: re-calling it re-registers the same keys.
 */
export function registerOfflineHandlers(): void {
  registerAppointmentHandlers()
  registerVisitReportHandlers()
  registerMissionDayHandlers()
  registerPlanningHandlers()
  registerCallResultHandlers()
}
